import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { buildMockPlatformSearchResult } from 'core/mocks/platform-search.mock';
import {
  PlatformSearchEntityType,
  PlatformSearchGroup,
  PlatformSearchItem,
  PlatformSearchResult,
} from 'core/models/platform-search-result.model';
import {
  defaultIconForSearchType,
  labelForSearchType,
} from 'core/utils/platform-search-route.util';
import { Observable, catchError, map, of, tap } from 'rxjs';

interface PlatformSearchApiItem {
  id: number | string;
  title: string;
  subtitle?: string | null;
  route?: string | null;
  route_hint?: string | null;
  icon_class?: string | null;
  meta?: Record<string, string | null | undefined> | null;
}

interface PlatformSearchApiGroup {
  type: string;
  label?: string | null;
  items: PlatformSearchApiItem[];
}

interface PlatformSearchApiResponse {
  ok: boolean;
  message: string;
  data: {
    query?: string;
    total?: number;
    groups: PlatformSearchApiGroup[];
  };
}

const ENTITY_TYPES: readonly PlatformSearchEntityType[] = [
  'user',
  'class',
  'enrollment',
  'institution',
  'academic_course',
  'subject',
  'page',
] as const;

@Injectable({ providedIn: 'root' })
export class PlatformSearchService {
  private readonly http = inject(HttpClient);

  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly lastResult = signal<PlatformSearchResult | null>(null);

  search(query: string): Observable<PlatformSearchResult> {
    const q = query.trim();

    if (!q) {
      const empty: PlatformSearchResult = { query: '', total: 0, groups: [] };
      this.lastResult.set(empty);
      this.loadError.set(null);
      return of(empty);
    }

    if (environment.useMockAuth) {
      const result = buildMockPlatformSearchResult(q);
      this.lastResult.set(result);
      this.loadError.set(null);
      return of(result);
    }

    this.loading.set(true);
    this.loadError.set(null);

    const params = new HttpParams().set('q', q);

    return this.http
      .get<PlatformSearchApiResponse>(`${environment.apiUrl}/search`, {
        params,
      })
      .pipe(
        map((response) => this.mapApiResponse(response, q)),
        tap((result) => {
          this.lastResult.set(result);
          this.loading.set(false);
        }),
        catchError((error: unknown) => {
          this.loading.set(false);
          const canUseLocalFallback = !environment.production;
          this.loadError.set(
            this.resolveErrorMessage(error, canUseLocalFallback),
          );
          if (canUseLocalFallback) {
            const fallback = buildMockPlatformSearchResult(q);
            this.lastResult.set(fallback);
            return of(fallback);
          }
          const empty: PlatformSearchResult = {
            query: q,
            total: 0,
            groups: [],
          };
          this.lastResult.set(empty);
          return of(empty);
        }),
      );
  }

  private mapApiResponse(
    response: PlatformSearchApiResponse,
    fallbackQuery: string,
  ): PlatformSearchResult {
    const rawGroups = response.data?.groups ?? [];
    const groups = rawGroups
      .map((group) => this.mapApiGroup(group))
      .filter((group): group is PlatformSearchGroup => group != null);

    const total =
      response.data?.total ??
      groups.reduce((sum, group) => sum + group.items.length, 0);

    return {
      query: response.data?.query?.trim() || fallbackQuery,
      total,
      groups,
    };
  }

  private mapApiGroup(
    group: PlatformSearchApiGroup,
  ): PlatformSearchGroup | null {
    const type = this.parseEntityType(group.type);
    if (!type) {
      return null;
    }

    const items = (group.items ?? [])
      .map((item) => this.mapApiItem(item, type))
      .filter((item): item is PlatformSearchItem => item != null);

    if (items.length === 0) {
      return null;
    }

    return {
      type,
      label: group.label?.trim() || labelForSearchType(type),
      items,
    };
  }

  private mapApiItem(
    item: PlatformSearchApiItem,
    type: PlatformSearchEntityType,
  ): PlatformSearchItem | null {
    const id = String(item.id ?? '').trim();
    const title = item.title?.trim();
    if (!id || !title) {
      return null;
    }

    const metaEntries = Object.entries(item.meta ?? {})
      .filter((entry): entry is [string, string] => {
        const value = entry[1];
        return typeof value === 'string' && value.trim().length > 0;
      })
      .map(([key, value]) => [this.toCamelKey(key), value.trim()] as const);

    return {
      id,
      type,
      title,
      subtitle: item.subtitle?.trim() || undefined,
      iconClass: item.icon_class?.trim() || defaultIconForSearchType(type),
      route: item.route?.trim() || undefined,
      routeHint: item.route_hint?.trim() || undefined,
      meta:
        metaEntries.length > 0 ? Object.fromEntries(metaEntries) : undefined,
    };
  }

  private parseEntityType(value: string): PlatformSearchEntityType | null {
    const normalized = value.trim().toLowerCase().replace(/-/g, '_');
    return ENTITY_TYPES.includes(normalized as PlatformSearchEntityType)
      ? (normalized as PlatformSearchEntityType)
      : null;
  }

  private toCamelKey(key: string): string {
    return key.replace(/_([a-z])/g, (_, letter: string) =>
      letter.toUpperCase(),
    );
  }

  private resolveErrorMessage(
    error: unknown,
    usedLocalFallback: boolean,
  ): string {
    if (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      (error as { status?: number }).status === 404
    ) {
      return usedLocalFallback
        ? 'Endpoint de busca ainda não disponível na API. Exibindo resultados locais de desenvolvimento.'
        : 'Endpoint de busca ainda não disponível na API.';
    }

    if (
      typeof error === 'object' &&
      error !== null &&
      'message' in error &&
      typeof (error as { message?: unknown }).message === 'string'
    ) {
      return (error as { message: string }).message;
    }

    return 'Não foi possível concluir a busca na plataforma.';
  }
}
